import {
  CalendarDays,
  Copy,
  Download,
  FileText,
  ImageIcon,
  Loader2,
  MessageSquare,
  Share2,
  Users,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { ministryIconSvg, ministryOption } from '@/components/MinistryIcon';
import { SimpleSelect } from '@/design-system/ui/Select';
import { Switch } from '@/design-system/ui/Switch';
import {
  type ShareFile,
  canCopyImage,
  canShareFiles,
  canvasSupported,
  copyImage,
  downloadFiles,
  renderCalendarImage,
  renderCalendarPdf,
  renderShareImage,
  renderShareImages,
  renderSharePdf,
  shareFiles,
} from '@/services/share';
import {
  type ShareDocument,
  type ShareScope,
  shareDocument,
  shareText,
  whatsappUrl,
} from '@escala/domain/share';
import { type CalendarDocument, calendarDocument } from '@escala/domain/shareCalendar';
import { useStore } from '@/store/AppStore';

type Format = 'image' | 'pdf' | 'text';
type ImageMode = 'single' | 'perEvent';
/** Escala: quem serve em cada evento. Calendário: só os eventos na grade do mês ou da semana. */
type Mode = 'schedule' | 'calendar';

type Preview =
  { status: 'loading' } | { status: 'ready'; files: ShareFile[]; urls: string[] } | { status: 'error' };

/** O que foi gerado e para qual pedido: outro pedido em andamento é "carregando". */
type Rendered = { request: object; result: Exclude<Preview, { status: 'loading' }> };

const FORMATS: { value: Format; label: string }[] = [
  { value: 'image', label: 'Imagem' },
  { value: 'pdf', label: 'PDF' },
  { value: 'text', label: 'Texto' },
];
const MODES: { value: Mode; label: ReactNode }[] = [
  {
    value: 'schedule',
    label: (
      <>
        <Users aria-hidden />
        Escala
      </>
    ),
  },
  {
    value: 'calendar',
    label: (
      <>
        <CalendarDays aria-hidden />
        Calendário
      </>
    ),
  },
];

/**
 * Exportar eventos. No modo **Escala** (o padrão), quem serve em cada evento: escolhe o
 * ministério (e, vindo da tela filtrada por membro, só as funções da pessoa), vê a prévia
 * e entrega como imagem, PDF ou texto. No modo **Calendário** (só vindo do Calendário, que
 * passa o mês ou a semana em `scope.range`), a grade com o título de cada evento por dia,
 * sem escala, em imagem ou PDF — para divulgar a agenda da igreja. No celular, a imagem e
 * o PDF vão pela folha de compartilhamento do sistema (direto para o WhatsApp); no
 * computador, baixam ou a imagem é copiada para colar.
 */
export default function ShareDialog({
  eventIds,
  scope = {},
  onClose,
}: {
  eventIds: string[];
  /** Os filtros da tela que abriu: ministério, membro e o rótulo do período. */
  scope?: ShareScope;
  onClose: () => void;
}) {
  const { state, lookup, org, today, actions } = useStore();
  const [ministryId, setMinistryId] = useState<string | null>(scope.ministryId ?? null);
  const [onlyMember, setOnlyMember] = useState(!!scope.memberId);
  const [mode, setMode] = useState<Mode>('schedule');
  const [format, setFormat] = useState<Format>('image');
  const range = scope.range ?? null;
  const calendar = mode === 'calendar' && !!range;
  const [imageMode, setImageMode] = useState<ImageMode>('single');
  // Desligado por padrão: a imagem sai do app (WhatsApp, redes), e a foto é dado pessoal.
  const [photos, setPhotos] = useState(false);
  const [busy, setBusy] = useState(false);

  const events = useMemo(
    () => eventIds.map((id) => state.events.find((e) => e.id === id)).filter((e) => e !== undefined),
    [eventIds, state.events]
  );
  const ministries = useMemo(
    () => state.ministries.filter((m) => events.some((e) => e.ministryIds.includes(m.id))),
    [events, state.ministries]
  );
  const memberName = scope.memberId ? lookup.members.get(scope.memberId)?.name : undefined;
  const doc = useMemo(
    () =>
      shareDocument(events, lookup, {
        ministryId,
        memberId: onlyMember ? scope.memberId : null,
        period: scope.period,
      }),
    [events, lookup, ministryId, onlyMember, scope.memberId, scope.period]
  );
  const calendarDoc = useMemo(
    () => (range ? calendarDocument(events, range, range.kind) : null),
    [events, range]
  );
  const text = useMemo(() => shareText(doc), [doc]);
  const empty = calendar ? !calendarDoc?.count : doc.events.length === 0;
  const perEvent = !calendar && imageMode === 'perEvent' && doc.events.length > 1;
  const hasPhotos = useMemo(
    () => doc.events.some((e) => e.sections.some((s) => s.rows.some((r) => r.people.some((p) => p.photo)))),
    [doc]
  );
  const withPhotos = photos && hasPhotos;
  const canRender = useMemo(() => canvasSupported(), []);
  const request = useMemo(
    () => ({ doc, format, perEvent, photos: withPhotos, calendarDoc: calendar ? calendarDoc : null }),
    [calendar, calendarDoc, doc, format, perEvent, withPhotos]
  );
  const [rendered, setRendered] = useState<Rendered | null>(null);
  const preview: Preview = rendered?.request === request ? rendered.result : { status: 'loading' };

  useEffect(() => {
    const { doc, format, perEvent, photos, calendarDoc } = request;

    if (format === 'text' || !canRender) return;
    if (calendarDoc ? !calendarDoc.count : !doc.events.length) return;

    let alive = true;
    let urls: string[] = [];
    const options = { org: org.name, color: org.color, generatedAt: today, iconSvg: ministryIconSvg, photos };

    const render = async (): Promise<{ files: ShareFile[]; pages: Blob[] }> => {
      if (calendarDoc && format === 'pdf') {
        const { pdf, pages } = await renderCalendarPdf(calendarDoc, options);

        return { files: [pdf], pages };
      }

      if (calendarDoc) {
        const image = await renderCalendarImage(calendarDoc, options);

        return { files: [image], pages: [image.blob] };
      }

      if (format === 'pdf') {
        const { pdf, pages } = await renderSharePdf(doc, options);

        return { files: [pdf], pages };
      }

      const files = perEvent ? await renderShareImages(doc, options) : [await renderShareImage(doc, options)];

      return { files, pages: files.map((f) => f.blob) };
    };

    render().then(
      ({ files, pages }) => {
        if (!alive) return;

        urls = pages.map((blob) => URL.createObjectURL(blob));
        setRendered({ request, result: { status: 'ready', files, urls } });
      },
      () => alive && setRendered({ request, result: { status: 'error' } })
    );

    return () => {
      alive = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [canRender, org.color, org.name, request, today]);

  const files = preview.status === 'ready' ? preview.files : [];
  const fileReady = canRender && !empty && preview.status === 'ready' && !busy;
  const shareable = canShareFiles(
    format === 'pdf'
      ? { type: 'application/pdf', name: 'escala.pdf' }
      : { type: 'image/png', name: 'escala.png' }
  );

  const attempt = async (fn: () => Promise<unknown>, ok: string | null, fail: string) => {
    setBusy(true);

    try {
      const done = await fn();

      if (ok && done !== false) actions.showToast(ok);
    } catch {
      actions.showToast(fail, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copyText = () => {
    const fail = () =>
      actions.showToast('Não foi possível copiar automaticamente. Selecione o texto da prévia.', {
        tone: 'error',
      });

    if (!navigator.clipboard?.writeText) return fail();

    navigator.clipboard
      .writeText(text)
      .then(() => actions.showToast('Texto copiado. Cole no WhatsApp ou onde preferir.'), fail);
  };

  const copy = fileCopy(format, files.length, calendar);
  const title = calendar && calendarDoc ? calendarDoc.title : doc.title;
  const changeMode = (next: Mode) => {
    setMode(next);
    // O calendário não tem a versão em texto.
    if (next === 'calendar' && format === 'text') setFormat('image');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="md:h-[calc(100dvh-48px)] md:w-[min(880px,calc(100vw-48px))]">
        <DialogHeader
          title={events.length === 1 ? 'Exportar evento' : 'Exportar eventos'}
          description={calendar && calendarDoc ? describeCalendar(calendarDoc) : describe(doc)}
        />

        <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-line-divider px-5 py-3 max-md:px-4 max-md:py-2.5">
          {range ? (
            <SegmentedControl
              aria-label="O que exportar"
              size="sm"
              className="max-md:flex max-md:w-full [&>button]:max-md:flex-1 [&>button]:max-md:justify-center"
              value={mode}
              onValueChange={changeMode}
              options={MODES}
            />
          ) : null}
          {!calendar && ministries.length > 1 ? (
            <SimpleSelect
              aria-label="Ministério"
              size="sm"
              className="w-56"
              value={ministryId ?? 'all'}
              onValueChange={(v) => setMinistryId(v === 'all' ? null : v)}
              options={[
                { value: 'all', label: 'Todos os ministérios', pin: true },
                ...ministries.map(ministryOption),
              ]}
            />
          ) : null}
          {!calendar && memberName ? (
            <label className="flex items-center gap-2 text-small text-ink-secondary">
              <Switch
                aria-label={`Só as funções de ${memberName}`}
                checked={onlyMember}
                onCheckedChange={setOnlyMember}
              />
              Só as funções de {memberName}
            </label>
          ) : null}
          {!calendar && format !== 'text' && hasPhotos ? (
            <label className="flex items-center gap-2 text-small text-ink-secondary">
              <Switch aria-label="Incluir fotos de perfil" checked={photos} onCheckedChange={setPhotos} />
              Incluir fotos de perfil
            </label>
          ) : null}
          <div className="flex-1" />
          <SegmentedControl
            aria-label="Formato"
            size="sm"
            value={format}
            onValueChange={setFormat}
            options={calendar ? FORMATS.filter((f) => f.value !== 'text') : FORMATS}
          />
        </div>

        <DialogBody className="bg-surface-subtle">
          {calendar ? (
            <p className="m-0 mb-3 text-caption text-ink-subtle">
              Os eventos de cada dia, sem a escala — para divulgar a agenda da igreja.
            </p>
          ) : null}
          {!calendar && format !== 'text' && hasPhotos ? (
            <p className="m-0 mb-3 text-caption text-ink-subtle">
              {photos
                ? 'Com as fotos de perfil. O arquivo sai do Live Gospel: envie só a quem concorda em aparecer.'
                : 'Cada pessoa aparece com as iniciais, sem a foto de perfil.'}
            </p>
          ) : null}
          {!calendar && format === 'image' && doc.events.length > 1 ? (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-caption text-ink-subtle">
                {perEvent
                  ? `${doc.events.length} imagens, uma por evento — mais fáceis de ler no celular.`
                  : 'Todos os eventos numa imagem só.'}
              </p>
              <SegmentedControl
                aria-label="Imagens"
                size="sm"
                value={imageMode}
                onValueChange={setImageMode}
                options={[
                  { value: 'single', label: 'Uma imagem' },
                  { value: 'perEvent', label: 'Uma por evento' },
                ]}
              />
            </div>
          ) : null}
          <PreviewArea
            format={format}
            empty={empty}
            emptyText={
              calendar
                ? 'Nenhum evento neste período.'
                : 'Nenhuma escala para compartilhar neste recorte. Troque o ministério para ver outras.'
            }
            canRender={canRender}
            preview={preview}
            text={text}
          />
        </DialogBody>

        <DialogFooter className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
          {format === 'text' ? (
            <>
              {empty ? (
                <Button disabled>
                  <MessageSquare aria-hidden />
                  WhatsApp
                </Button>
              ) : (
                <a
                  href={whatsappUrl(text)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-control bg-success text-small font-semibold text-white hover:bg-status-complete hover:text-white"
                >
                  <MessageSquare aria-hidden className="size-4 stroke-[1.8]" />
                  WhatsApp
                </a>
              )}
              <Button variant="secondary" disabled={empty} onClick={copyText}>
                <Copy aria-hidden />
                Copiar texto
              </Button>
            </>
          ) : (
            <>
              {shareable ? (
                <Button
                  disabled={!fileReady}
                  onClick={() =>
                    attempt(
                      () => shareFiles(files, title),
                      null,
                      'Não foi possível compartilhar. Baixe o arquivo e envie pelo WhatsApp.'
                    )
                  }
                >
                  <Share2 aria-hidden />
                  Compartilhar
                </Button>
              ) : null}
              <Button
                variant={shareable ? 'secondary' : 'primary'}
                disabled={!fileReady}
                onClick={() =>
                  attempt(() => downloadFiles(files), copy.downloaded, 'Não foi possível baixar o arquivo.')
                }
              >
                <Download aria-hidden />
                {copy.download}
              </Button>
              {format === 'image' && files.length === 1 && canCopyImage() ? (
                <Button
                  variant="secondary"
                  disabled={!fileReady}
                  onClick={() =>
                    attempt(
                      () => copyImage(files[0]!),
                      'Imagem copiada. Cole na conversa do WhatsApp.',
                      'Não foi possível copiar a imagem. Baixe e envie pelo WhatsApp.'
                    )
                  }
                >
                  <Copy aria-hidden />
                  Copiar imagem
                </Button>
              ) : null}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Rótulo do botão e aviso do download, por formato e quantidade de arquivos. */
function fileCopy(
  format: Format,
  count: number,
  calendar: boolean
): { download: string; downloaded: string } {
  if (calendar) {
    return format === 'pdf'
      ? { download: 'Baixar PDF', downloaded: 'PDF do calendário baixado' }
      : { download: 'Baixar imagem', downloaded: 'Imagem do calendário baixada' };
  }

  if (format === 'pdf') return { download: 'Baixar PDF', downloaded: 'PDF da escala baixado' };

  return count > 1
    ? { download: 'Baixar imagens', downloaded: 'Imagens da escala baixadas' }
    : { download: 'Baixar imagem', downloaded: 'Imagem da escala baixada' };
}

function describe(doc: ShareDocument): string {
  const [only] = doc.events;

  if (!only) return 'Nenhuma escala no recorte';

  return doc.events.length === 1 ? `${only.title} · ${only.when}` : `${doc.period} · ${doc.subtitle}`;
}

function describeCalendar(doc: CalendarDocument): string {
  return doc.kind === 'week' ? doc.subtitle : `${doc.period} · ${doc.subtitle}`;
}

function PreviewArea({
  format,
  empty,
  emptyText,
  canRender,
  preview,
  text,
}: {
  format: Format;
  empty: boolean;
  emptyText: string;
  canRender: boolean;
  preview: Preview;
  text: string;
}): ReactNode {
  if (empty) {
    return <p className="m-0 py-10 text-center text-small text-ink-subtle">{emptyText}</p>;
  }

  if (format === 'text') {
    return (
      <>
        <p className="m-0 mb-1.5 text-caption font-semibold text-ink-subtle">Prévia da mensagem</p>
        <pre className="m-0 rounded-block border border-status-complete-line bg-success-soft p-3.5 font-sans text-small whitespace-pre-wrap text-status-complete">
          {text}
        </pre>
      </>
    );
  }

  const Icon = format === 'pdf' ? FileText : ImageIcon;

  if (!canRender || preview.status === 'error') {
    return (
      <p className="m-0 flex flex-col items-center gap-2 py-10 text-center text-small text-ink-subtle">
        <Icon aria-hidden className="size-6 stroke-[1.8]" />
        {canRender
          ? 'Não foi possível gerar a prévia. Tente de novo ou use o texto.'
          : 'Este navegador não gera a imagem nem o PDF. Use o texto.'}
      </p>
    );
  }

  if (preview.status === 'loading') {
    return (
      <p
        role="status"
        className="m-0 flex flex-col items-center gap-2 py-10 text-center text-small text-ink-subtle"
      >
        <Loader2 aria-hidden className="size-6 animate-spin stroke-[1.8]" />
        {format === 'pdf' ? 'Montando o PDF…' : 'Gerando a imagem…'}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {preview.urls.map((url, i) => (
        <img
          key={url}
          src={url}
          alt={`${format === 'pdf' ? 'Página' : 'Imagem'} ${i + 1} de ${preview.urls.length}`}
          className="block w-full rounded-block border border-line-card shadow-raised"
        />
      ))}
    </div>
  );
}
